'use client';

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COUNTRY_KEYS } from '@/lib/constants';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface RepresentativeFormData {
  addTo: string;
  role: string;
  country: string;
  citizenship: boolean;
  personalId: string;
  firstName: string;
  lastName: string;
  email: string;
  addressCountry: string;
  numberOfShares: string;
}

interface AddRepresentativeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddRepresentative: (data: RepresentativeFormData) => void;
  totalShares: number;
}

const roleOptions = [
  'shareholder',
  'boardMember',
  'boardDeputy',
  'ceo',
  'viceCeo',
  'externalSignatory',
  'authorizingPerson',
];

export default function AddRepresentativeDialog({
  open,
  onOpenChange,
  onAddRepresentative,
  totalShares = 250,
}: Readonly<AddRepresentativeDialogProps>): JSX.Element {
  const { t } = useLanguage();

  const [formData, setFormData] = useState<RepresentativeFormData>({
    addTo: 'newPerson',
    role: 'shareholder',
    country: 'sweden',
    citizenship: false,
    personalId: '',
    firstName: '',
    lastName: '',
    email: '',
    addressCountry: 'sweden',
    numberOfShares: '',
  });

  const handleInputChange = (field: keyof RepresentativeFormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onAddRepresentative(formData);
    // Reset form
    setFormData({
      addTo: 'newPerson',
      role: 'shareholder',
      country: 'sweden',
      citizenship: false,
      personalId: '',
      firstName: '',
      lastName: '',
      email: '',
      addressCountry: 'sweden',
      numberOfShares: '',
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset form when cancelling
    setFormData({
      addTo: 'newPerson',
      role: 'shareholder',
      country: 'sweden',
      citizenship: false,
      personalId: '',
      firstName: '',
      lastName: '',
      email: '',
      addressCountry: 'sweden',
      numberOfShares: '',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle>{t.addOwnerRepresentativeTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Top Section with Add To and Role */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="addTo">{t.addToLabel}</Label>
              <Select
                value={formData.addTo}
                onValueChange={(value) => handleInputChange('addTo', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.newPersonPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newPerson">{t.newPerson}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t.asRoleLabel}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleInputChange('role', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.shareholder} />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role} value={role}>
                      {t.roles[role as keyof typeof t.roles]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Personal Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t.personalDetailsTitle}</h3>

            <div className="space-y-2">
              <Label htmlFor="country">{t.registeredIn}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => handleInputChange('country', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.countryPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_KEYS.map((countryKey) => (
                    <SelectItem key={countryKey} value={countryKey}>
                      {t.countries[countryKey as keyof typeof t.countries]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="citizenship"
                checked={formData.citizenship}
                onCheckedChange={(checked) => handleInputChange('citizenship', Boolean(checked))}
              />
              <Label htmlFor="citizenship">{t.citizenshipOtherCountry}</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="personalId">{t.personalIdFormat}</Label>
              <Input
                id="personalId"
                value={formData.personalId}
                onChange={(e) => handleInputChange('personalId', e.target.value)}
                placeholder="ÅÅÅÅMMDD-XXXX"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t.firstName}</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">{t.lastName}</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.emailAddress}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="addressCountry">{t.country}</Label>
              <Select
                value={formData.addressCountry}
                onValueChange={(value) => handleInputChange('addressCountry', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.countryPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_KEYS.map((countryKey) => (
                    <SelectItem key={countryKey} value={countryKey}>
                      {t.countries[countryKey as keyof typeof t.countries]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Shares Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t.sharesTitle}</h3>
            <div className="space-y-2">
              <Label htmlFor="numberOfShares">
                {t.numberOfSharesLabel} ({t.ofTotal} {totalShares})
              </Label>
              <Input
                id="numberOfShares"
                type="number"
                value={formData.numberOfShares}
                onChange={(e) => handleInputChange('numberOfShares', e.target.value)}
                max={totalShares}
                min="1"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={handleCancel}>
            {t.cancel}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            {t.addButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
